import clsx from 'clsx';
import Image from 'next/image';
import { Icon } from 'common/icon';
import { ICasino, ISlot, ISlotReview } from 'constants/types';
import dayjs from 'dayjs';
import Link from 'next/link';
import { Banner } from 'components/banner';
import { Routes } from 'constants/routes';
import { getItemIdentifier } from 'utils/slug';

import styles from './styles.module.css';
import { useMemo, useState } from 'react';
import { ConfirmModal } from 'components/modal/confirm-modal';
import { useDeleteSlotReviewMutation } from 'hooks/api/slot_reviews';

const getCasinoIconUrl = (casino?: ICasino) =>
  casino?.imageUrl || casino?.logoUrl || '';

const getSlotImageUrl = (slot?: ISlot) => slot?.imageUrl || slot?.logoUrl || '';

export const ReviewSlot = ({
  data,
  casino,
  slot,
  isPublicProfile,
  variant,
}: {
  slot?: ISlot;
  casino?: ICasino;
  data: ISlotReview;
  isPublicProfile: boolean;
  variant: 'slot' | 'user';
}) => {
  const { averageRatingHint, averageRatingClass, icon } = useMemo(() => {
    if (data.value >= 95) {
      return {
        averageRatingHint: 'high RTP',
        averageRatingClass: styles.highRtp,
        icon: <Icon.Cherry width="14" height="14" />,
      };
    }

    return {
      averageRatingHint: 'Reduced RTP',
      averageRatingClass: styles.lowRtp,
      icon: <Icon.Bad width="14" height="14" />,
    };
  }, [data.value]);
  const [deleteSlotReview] = useDeleteSlotReviewMutation();
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const casinoIconUrl = getCasinoIconUrl(casino);
  const slotImageUrl = getSlotImageUrl(slot);
  const userImageUrl = data?.user?.imageUrl ?? '';
  const handleCancelDeletion = () => {
    setIsDeleteModalVisible(false);
  };
  const handleDelete = async () => {
    try {
      await deleteSlotReview({ id: data?._id });
    } catch (error) {
      console.error('Ошибка при удалении слота:', error);
    }
  };

  return (
    <>
      <div className={styles.reviews__column}>
        <div className={clsx(styles.reviews__review, styles.slot_review)}>
          <div className={styles.header}>
            {variant === 'slot' && (
              <Link
                href={Routes.casinoPage(getItemIdentifier(casino))}
                className={clsx(
                  styles.userSlot_container,
                  styles.slotContainer
                )}
              >
                <Image
                  width={20}
                  height={20}
                  src={casinoIconUrl}
                  alt="slot-review"
                  className={styles.casino_image}
                />
                {casino?.name}
              </Link>
            )}
            {variant === 'user' && (
              <div className={styles.userChecked}>
                <Link
                  className={styles.userSlot_container}
                  href={Routes.publicProfile(data?.user?._id ?? '')}
                >
                  <Image
                    width={20}
                    height={20}
                    src={userImageUrl}
                    alt="user-slot-review"
                    className={styles.userSlot_image}
                  />
                  {data?.user?.username}
                </Link>
                <span className={styles.checkedText}>checked</span>
                {casino?.name && (
                  <Link
                    href={Routes.casinoPage(getItemIdentifier(casino))}
                    className={clsx(
                      styles.userSlot_container,
                      styles.slotContainer
                    )}
                  >
                    <Image
                      width={20}
                      height={20}
                      src={casinoIconUrl}
                      alt="casino-checked-review"
                      className={styles.casino_image}
                    />
                    {casino?.name}
                  </Link>
                )}
              </div>
            )}
            {!data.isReviewed && <Banner text={'Under review'} />}
            {!isPublicProfile && (
              <button
                className={styles.binButton}
                onClick={() => setIsDeleteModalVisible(true)}
              >
                <Icon.Bin />
              </button>
            )}
          </div>
          <Link
            href={Routes.slotPage(getItemIdentifier(slot))}
            className={styles.slot_review__slot_link}
          >
            <div className={styles.slot_review__info}>
              <div className={styles.slot_review__photo}>
                <Image
                  width={50}
                  height={50}
                  src={slotImageUrl}
                  alt="slot-review"
                />
              </div>
              <div className={styles.slot_review__about}>
                <div className={styles.slot_review__name}>
                  {slot?.name ?? 'Slot'}
                </div>
                <div className={styles.slot_review__date}>
                  {dayjs(data.createdAt).format('DD MMM, YYYY')}
                </div>
              </div>
            </div>
            <div className={clsx(styles.slot_review__rtpi, averageRatingClass)}>
              {`${(data?.value ?? 0)?.toFixed(2)}%`}
              <div className={clsx(styles.ratingHint, averageRatingClass)}>
                {icon}
                <span>{averageRatingHint}</span>
              </div>
            </div>
          </Link>
        </div>
      </div>
      <ConfirmModal
        headerTitle="Delete"
        isVisible={isDeleteModalVisible}
        onSubmit={handleDelete}
        onClose={handleCancelDeletion}
        title="Do you want to delete review?"
      />
    </>
  );
};
